/**
 * Geometry & Shape games: 2D/3D shapes, angles, symmetry, perimeter/area, volume.
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const makeNumericDistractors = (correct, count = 3) => {
    const base = Number(correct);
    const seen = new Set([String(base)]);
    const out = [];
    const offsets = [-4, -3, -2, -1, 1, 2, 3, 4];
    while (out.length < count) {
        const off = offsets[Math.floor(Math.random() * offsets.length)];
        const val = base + off;
        if (!seen.has(String(val)) && val >= 0) {
            seen.add(String(val));
            out.push(String(val));
        }
    }
    return out;
};

/** Identifying 2D and 3D shapes and their properties */
export const generateShapePropertiesQuestion = (difficulty = "normal") => {
    const questions = [
        {
            q: "Which of these is a 3D shape?",
            answer: "Cube",
            options: ["Square", "Rectangle", "Cube", "Circle"],
        },
        {
            q: "How many sides does a hexagon have?",
            answer: "6",
            numeric: true,
        },
        {
            q: "How many faces does a cube have?",
            answer: "6",
            numeric: true,
        },
        {
            q: "Which shape has exactly 3 sides?",
            answer: "Triangle",
            options: ["Triangle", "Square", "Pentagon", "Hexagon"],
        },
        {
            q: "A circle is best described as:",
            answer: "A 2D shape with no straight sides",
            options: [
                "A 2D shape with no straight sides",
                "A 3D shape with flat faces",
                "A 2D shape with four equal sides",
                "A 3D shape with 6 faces",
            ],
        },
    ];

    const item = questions[Math.floor(Math.random() * questions.length)];
    let options;
    if (item.numeric) {
        options = shuffle([item.answer, ...makeNumericDistractors(item.answer, 3)]);
    } else {
        options = shuffle(item.options);
    }

    return {
        question: item.q,
        answer: item.answer,
        options,
        type: "geometry-2d-3d",
        mode: "shapes",
        visualType: "shape-props",
        shapeRef: item.q.includes("3D") ? "cube" : item.q.includes("hexagon") ? "hexagon" : item.q.includes("faces") ? "cube" : item.q.includes("3 sides") ? "triangle" : item.q.includes("circle") ? "circle" : "square",
    };
};

/** Angles — measuring, calculating, recognising types */
export const generateAnglesQuestion = (difficulty = "normal") => {
    const angleTypes = [
        { size: 45, type: "acute" },
        { size: 90, type: "right" },
        { size: 120, type: "obtuse" },
        { size: 180, type: "straight" },
    ];

    const pick = angleTypes[Math.floor(Math.random() * angleTypes.length)];

    const mode = Math.random() < 0.5 ? "type" : "calculate";

    if (mode === "type") {
        const question = `An angle of ${pick.size}° is what type of angle?`;
        const answer = pick.type;
        const options = shuffle(["acute", "right", "obtuse", "straight"]);
        return {
            question,
            answer,
            options,
            type: "geometry-angles",
            mode: "angles",
            visualType: "angle-type",
            angleSize: pick.size,
        };
    }

    // Simple calculation using facts: straight line (180°) or right angle (90°)
    if (Math.random() < 0.5) {
        const a = Math.floor(Math.random() * 80) + 20;
        const b = 180 - a;
        const question = `Two angles lie on a straight line. One is ${a}°. What is the other angle?`;
        const answer = String(b);
        const options = shuffle([answer, ...makeNumericDistractors(answer, 3)]);
        return {
            question,
            answer,
            options,
            type: "geometry-angles",
            mode: "angles",
            visualType: "angle-calc",
            angle1: a,
            total: 180,
        };
    } else {
        const a = Math.floor(Math.random() * 60) + 10;
        const b = 90 - a;
        const question = `Two angles make a right angle (90°). One is ${a}°. What is the other angle?`;
        const answer = String(b);
        const options = shuffle([answer, ...makeNumericDistractors(answer, 3)]);
        return {
            question,
            answer,
            options,
            type: "geometry-angles",
            mode: "angles",
            visualType: "angle-calc",
            angle1: a,
            total: 90,
        };
    }
};

/** Symmetry: reflection and rotational */
export const generateSymmetryQuestion = (difficulty = "normal") => {
    const questions = [
        {
            q: "How many lines of symmetry does a square have?",
            answer: "4",
            numeric: true,
        },
        {
            q: "How many lines of symmetry does an equilateral triangle have?",
            answer: "3",
            numeric: true,
        },
        {
            q: "Which shape has rotational symmetry of order 4?",
            answer: "Square",
            options: ["Square", "Rectangle", "Equilateral triangle", "Circle"],
        },
        {
            q: "A shape that looks the same in a mirror line has:",
            answer: "Reflection symmetry",
            options: [
                "Reflection symmetry",
                "Rotational symmetry",
                "No symmetry",
                "Parallel lines",
            ],
        },
    ];

    const item = questions[Math.floor(Math.random() * questions.length)];
    let options;
    if (item.numeric) {
        options = shuffle([item.answer, ...makeNumericDistractors(item.answer, 3)]);
    } else {
        options = shuffle(item.options);
    }

    return {
        question: item.q,
        answer: item.answer,
        options,
        type: "geometry-symmetry",
        mode: "symmetry",
        visualType: "symmetry",
    };
};

/** Perimeter and area of shapes (rectangles and simple shapes) */
export const generatePerimeterAreaQuestion = (difficulty = "normal") => {
    const useArea = Math.random() < 0.5;
    const a = Math.floor(Math.random() * 8) + 3; // 3–10
    const b = Math.floor(Math.random() * 8) + 3;

    if (useArea) {
        const area = a * b;
        const question = `A rectangle is ${a} cm long and ${b} cm wide. What is its area?`;
        const answer = String(area);
        const options = shuffle([answer, ...makeNumericDistractors(answer, 3)]).map(
            (opt) => opt
        );
        return {
            question,
            answer,
            options,
            type: "geometry-perimeter-area",
            mode: "perimeter-area",
            visualType: "rectangle",
            dimensions: { length: a, width: b },
            measure: "area",
        };
    } else {
        const perimeter = 2 * (a + b);
        const question = `A rectangle is ${a} cm long and ${b} cm wide. What is its perimeter?`;
        const answer = String(perimeter);
        const options = shuffle([answer, ...makeNumericDistractors(answer, 3)]);
        return {
            question,
            answer,
            options,
            type: "geometry-perimeter-area",
            mode: "perimeter-area",
            visualType: "rectangle",
            dimensions: { length: a, width: b },
            measure: "perimeter",
        };
    }
};

/** Volume of simple solids (cuboids/cubes) */
export const generateVolumeQuestion = (difficulty = "normal") => {
    const isCube = Math.random() < 0.5;
    if (isCube) {
        const s = Math.floor(Math.random() * 5) + 2;
        const volume = s * s * s;
        const question = `A cube has side length ${s} cm. What is its volume?`;
        const answer = String(volume);
        const options = shuffle([answer, ...makeNumericDistractors(answer, 3)]);
        return {
            question,
            answer,
            options,
            type: "geometry-volume",
            mode: "volume",
            visualType: "cube",
            side: s,
        };
    } else {
        const l = Math.floor(Math.random() * 5) + 2;
        const w = Math.floor(Math.random() * 5) + 2;
        const h = Math.floor(Math.random() * 5) + 2;
        const volume = l * w * h;
        const question = `A cuboid is ${l} cm long, ${w} cm wide and ${h} cm high. What is its volume?`;
        const answer = String(volume);
        const options = shuffle([answer, ...makeNumericDistractors(answer, 3)]);
        return {
            question,
            answer,
            options,
            type: "geometry-volume",
            mode: "volume",
            visualType: "cuboid",
            dimensions: { l, w, h },
        };
    }
};

export const generateGeometryShapeQuestionByKey = (gameKey, difficulty = "normal") => {
    switch (gameKey) {
        case "geometry-2d-3d":
            return generateShapePropertiesQuestion(difficulty);
        case "geometry-angles":
            return generateAnglesQuestion(difficulty);
        case "geometry-symmetry":
            return generateSymmetryQuestion(difficulty);
        case "geometry-perimeter-area":
            return generatePerimeterAreaQuestion(difficulty);
        case "geometry-volume":
            return generateVolumeQuestion(difficulty);
        default:
            return generateShapePropertiesQuestion(difficulty);
    }
};

